import { Stack } from '@mui/material'
import { useContext } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { generatePath } from 'react-router-dom'

import { GapBox } from 'components/Box'
import { BoxForm, RadioGroup, Switch } from 'components/Form'
import { SectionHeader } from 'components/Header'
import Paper from 'components/Paper'
import { RelatedList, RelatedListItem, RelatedSettings } from 'components/RelatedSettings'
import { AddEditAdUnitContext } from 'features/inventory/ad-unit/contexts/AddEditAdUnitContext'
import { PrebidSettingsAlert } from 'features/inventory/domain/forms/UnfilledRecoveryForm/components/PrebidSettingsAlert'
import { usePrebidBehavioursRadioGroup } from 'features/inventory/domain/forms/UnfilledRecoveryForm/hooks/usePrebidBehavioursRadioGroup'
import { useAbility } from 'providers/casl'
import { CONTAINER_SPACING } from 'providers/material-ui/theme/constants'
import { ROUTES } from 'routes'
import { Schema } from '../schema'

export const PrebidSettingsSection = (): JSX.Element => {
  const { t } = useTranslation('features/adUnit', {
    keyPrefix: 'form.unfilledRecovery.prebidSettings',
  })
  const { t: tDomain } = useTranslation('features/domain')
  const { adUnit } = useContext(AddEditAdUnitContext)
  const { control, watch } = useFormContext<Schema>()

  const ability = useAbility()
  const cannotUpdateAdUnit = ability.cannot('update', 'AdUnit')
  const cannotUpdateUnfilledRecovery = ability.cannot('update', 'Product-unfilledRecovery')
  const noPrebidStackAccess = ability.cannot('access', 'Product-prebidStack')
  const canReadPrebidSettingsWarning: boolean = ability.can(
    'read',
    'AdUnitFeature-prebidSettingsWarning',
  )

  const { customizePrebidBehaviour, prebidBehaviourType } = watch()

  const prebidBehavioursOptions = usePrebidBehavioursRadioGroup()

  if (!adUnit) {
    throw Error('Ad unit should be accessible')
  }

  const {
    adUnitUnfilledRecovery: {
      prebidSettingsWarnings: {
        allRecoveryAdUnitDisabled,
        multipleBasePathUnitsDetected,
        prebidStackDisabled,
        recoveryAdUnitNotAdded,
      },
      prebidSettingsWarnings,
    },
    domain: {
      domainUnfilledRecovery: { prebidBehaviourType: domainPrebidBehaviourType },
      id: domainId,
      workspace: { id: workspaceId },
    },
  } = adUnit

  const showPrebidSettingsWarning: boolean =
    prebidBehaviourType === 'runRecoveryPrebidAuction' &&
    canReadPrebidSettingsWarning &&
    (allRecoveryAdUnitDisabled ||
      prebidStackDisabled ||
      recoveryAdUnitNotAdded ||
      multipleBasePathUnitsDetected)

  const domainSettingsUrl: string = generatePath(ROUTES.INVENTORY.DOMAIN.EDIT, {
    id: domainId,
    tab: 'unfilled-recovery',
    workspaceId,
  })

  return (
    <Paper data-cy='section-unfilled-recovery-prebid-settings'>
      <SectionHeader
        subtitle={t('subtitle')}
        title={t('title')}
      />

      <GapBox />

      <Stack direction='row'>
        <BoxForm>
          <Switch
            control={control}
            disabled={cannotUpdateAdUnit || cannotUpdateUnfilledRecovery || noPrebidStackAccess}
            label={t('customizePrebidBehaviour')}
            labelPlacement='start'
            name='customizePrebidBehaviour'
          />

          {customizePrebidBehaviour && (
            <BoxForm sx={{ ml: CONTAINER_SPACING }}>
              <RadioGroup
                control={control}
                disabled={cannotUpdateAdUnit || cannotUpdateUnfilledRecovery || noPrebidStackAccess}
                label={t('prebidBehaviourType')}
                name='prebidBehaviourType'
                options={prebidBehavioursOptions}
              />
            </BoxForm>
          )}
        </BoxForm>

        {customizePrebidBehaviour && (
          <RelatedSettings title={t('relatedSettings.title')}>
            <RelatedList>
              <RelatedListItem
                content={t('relatedSettings.content')}
                url={domainSettingsUrl}
                value={tDomain(
                  `form.unfilledRecovery.prebidSettings.prebidBehaviourType.values.${domainPrebidBehaviourType}.content`,
                )}
              />
            </RelatedList>
          </RelatedSettings>
        )}
      </Stack>

      {customizePrebidBehaviour && showPrebidSettingsWarning && (
        <PrebidSettingsAlert warnings={prebidSettingsWarnings} />
      )}
    </Paper>
  )
}
